import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { CheckCircle, XCircle, UserCheck, UserMinus } from 'lucide-react'
import api from '../../services/api'

export default function PermitDetail() {
  const { id } = useParams<{ id: string }>()
  const [permit, setPermit] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState(false)

  const load = () => api.get(`/permits/${id}`).then(r => setPermit(r.data)).finally(() => setLoading(false))

  useEffect(() => { load() }, [id])

  const action = async (endpoint: string) => {
    setActing(true)
    try { await api.post(`/permits/${id}/${endpoint}`); await load() } catch {}
    setActing(false)
  }

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading...</div>
  if (!permit) return null

  const signedOn = JSON.parse(permit.signed_on || '[]')
  const signedOff = JSON.parse(permit.signed_off || '[]')
  const hazards = JSON.parse(permit.hazards_identified || '[]')
  const controls = JSON.parse(permit.controls_required || '[]')

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Link to="/permits" className="text-sm" style={{ color: 'var(--accent)' }}>← Permits</Link>
          <h1 className="text-2xl font-bold mt-1" style={{ color: 'var(--text)' }}>{permit.permit_number}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            {permit.type_config?.label || permit.permit_type} · {permit.site_name || 'No site'}
          </p>
        </div>
        <span className="badge badge-high capitalize">{permit.status?.replace('_', ' ')}</span>
      </div>

      <div className="card space-y-4">
        <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Work Details</h2>
        <p style={{ color: 'var(--text)' }}>{permit.work_description}</p>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><strong>Location:</strong> {permit.location_description}</p>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          <strong>Valid:</strong> {new Date(permit.valid_from).toLocaleString('en-AU')} → {new Date(permit.valid_to).toLocaleString('en-AU')}
        </p>
      </div>

      {hazards.length > 0 && (
        <div className="card">
          <h2 className="font-semibold mb-3" style={{ color: 'var(--text)' }}>Hazards</h2>
          <ul className="space-y-1">
            {hazards.map((h: string, i: number) => <li key={i} className="text-sm flex items-center gap-2" style={{ color: 'var(--text)' }}><XCircle size={14} style={{ color: 'var(--red)' }} />{h}</li>)}
          </ul>
        </div>
      )}

      {controls.length > 0 && (
        <div className="card">
          <h2 className="font-semibold mb-3" style={{ color: 'var(--text)' }}>Controls</h2>
          <ul className="space-y-1">
            {controls.map((c: string, i: number) => <li key={i} className="text-sm flex items-center gap-2" style={{ color: 'var(--text)' }}><CheckCircle size={14} style={{ color: 'var(--green)' }} />{c}</li>)}
          </ul>
        </div>
      )}

      {/* Sign on/off */}
      <div className="card space-y-4">
        <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Personnel Sign-On/Off</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Signed On ({signedOn.length})</h3>
            {signedOn.map((s: any, i: number) => <div key={i} className="text-sm badge badge-low mb-1">{s.name}</div>)}
          </div>
          <div>
            <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Signed Off ({signedOff.length})</h3>
            {signedOff.map((s: any, i: number) => <div key={i} className="text-sm badge badge-medium mb-1">{s.name}</div>)}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {permit.status === 'draft' && (
          <button onClick={() => action('approve')} disabled={acting} className="btn btn-primary">
            <CheckCircle size={16} /> Approve Permit
          </button>
        )}
        {permit.status === 'approved' && (
          <button onClick={() => action('sign-on')} disabled={acting} className="btn btn-primary">
            <UserCheck size={16} /> Sign On
          </button>
        )}
        {permit.status === 'active' && (
          <>
            <button onClick={() => action('sign-on')} disabled={acting} className="btn btn-secondary">
              <UserCheck size={16} /> Sign On
            </button>
            <button onClick={() => action('sign-off')} disabled={acting} className="btn btn-secondary">
              <UserMinus size={16} /> Sign Off
            </button>
            <button onClick={() => action('close')} disabled={acting} className="btn btn-danger">
              Close Permit
            </button>
          </>
        )}
      </div>
    </div>
  )
}
